import React, { useState } from 'react';
import {
  KFDataTable,
  KFDataTableColDef,
  KFButton,
  KFDataTableProps,
} from '@kf/ic2fx';
import { Grid, Typography } from '@mui/material';
import { colors } from '../../../styles/colors';
import { dateFormat } from 'utilities/formatDate';
import { IReqCandidatesListItem } from 'modules/hiring/models/IRequisitionCandidates.interface';
import { Link, useNavigate } from 'react-router-dom';
import { HMRoutingPaths } from 'modules/hiring/models/HMRoutingPaths';
import TypographyWithI18N from 'shared/components/TypographyWithI18N';
import { calculateTimeInStep } from '../utilities';
import {
  MakeDecisionStatus,
  TMakeDecisionStatus,
  getMakeDecisionButtonStatus,
  shouldMakeDecision,
} from '../models/hiringDecisionOption';
import {
  HiringActivity,
  HMStatusListForInterview,
  HMStatusListForScreen,
  THMAcitivityStatus,
} from '../models/candidatePipeline.model';
import MakeDecisionModal, { IMakeDecisionData } from './decisionModal';
import { getDecisionTag } from './decisionTag';
import { useTranslation, WithTranslation, withTranslation } from 'react-i18next';
interface ICandidatePipeLineTableProps
  extends WithTranslation,
    Required<
      Pick<
        KFDataTableProps,
        | 'page'
        | 'pageSize'
        | 'onPageChange'
        | 'onPageSizeChange'
        | 'rowCount'
        /** Pass true for rendering more than 3 columns in unit testing. */
        | 'disableVirtualization'
      >
    > {
  requisitionCandidates: IReqCandidatesListItem[] | undefined;
  showLoader: boolean;
}

const CandidatePipeLineTable: React.FC<ICandidatePipeLineTableProps> = ({
  requisitionCandidates,
  showLoader,
  ...props
}) => {
  const { t: translate } = useTranslation();
  const [makeDecisionModalOpen, setMakeDecisionModalOpen] = useState(false);
  const [makingDecisionFor, setMakingDecisionFor] =
    useState<IMakeDecisionData>();
  const navigate = useNavigate();

  const columns: KFDataTableColDef[] = [
    {
      field: 'name',
      sortable: false,
      renderHeader: () => (
        <Typography
          className='kfic2-bodyTextBold'
          id='nameId'
          data-testid='nameId'
          sx={{ pl: '1rem' }}
        >
          {translate('name')}
        </Typography>
      ),
      flex: 1,
      disableColumnMenu: true,
      renderCell: ({ row: candidate }: { row: IReqCandidatesListItem }) => {
        return (
          <Link
            to={`../${HMRoutingPaths.candidateProfile}`}
            state={{ candidate }}
            title={candidate.name}
            className='MuiDataGrid-cellContent kfic2-bodyTextLink'
            style={{
              marginLeft: '1rem',
              textUnderlineOffset: 3,
            }}
            id={`name-${candidate?.applicationID}`}
            data-testid={`name-${candidate?.applicationID}`}
          >
            {candidate.name}
          </Link>
        );
      },
    },
    {
      field: 'jobTitle',
      sortable: false,
      renderHeader: () => (
        <Typography
          className='kfic2-bodyTextBold'
          title={translate('requisition') as string}
          id='jobTitleID'
          data-testid='jobTitleID'
        >
          {translate('requisition')}
        </Typography>
      ),
      flex: 1,
      disableColumnMenu: true,
    },
    {
      field: 'requisitionID',
      sortable: false,
      renderHeader: () => (
        <Typography
          className='kfic2-bodyTextBold'
          id='jobId'
          data-testid='jobId'
        >
          {translate('requisitionId')}
        </Typography>
      ),
      flex: 1,
      disableColumnMenu: true,
    },
    {
      field: 'activityName',
      sortable: false,
      renderHeader: () => (
        <Typography
          className='kfic2-bodyTextBold'
          id='stage'
          data-testid='stage'
        >
          {translate('stage')}
        </Typography>
      ),
      flex: 1,
      disableColumnMenu: true,
      renderCell: ({ row }: { row: IReqCandidatesListItem }) => {
        const timeStamp = row.activityCreatedDate;
        return (
          <Grid direction='column'>
            <Typography
              className='kfic2-bodyText'
              id={`stage-${row.applicationID}`}
              data-testid={`stage-${row.applicationID}`}
              title={row.activityName}
            >
              {row.activityName}
            </Typography>
            <Typography
              className='kfic2-bodyText'
              id={`timeInDays-${row.applicationID}`}
              data-testid={`timeInDays-${row.applicationID}`}
              sx={{ color: `${colors?.tertiary?.darkGray} !important` }}
            >
              {calculateTimeInStep(timeStamp)}
            </Typography>
          </Grid>
        );
      },
    },
    {
      field: 'activityStatus',
      sortable: false,
      renderHeader: () => (
        <Typography
          className='kfic2-bodyTextBold'
          id='status'
          data-testid='status'
        >
          {translate('status')}
        </Typography>
      ),
      flex: 1,
      disableColumnMenu: true,
      renderCell: ({ row }: { row: IReqCandidatesListItem }) => {
        return (
          <Grid direction='column'>
            <Typography
              className='kfic2-bodyText'
              id={`stage-${row.applicationID}`}
              data-testid={`stage-${row.applicationID}`}
              title={row.activityStatus}
            >
              {row.activityStatus}
            </Typography>
            <Typography
              className='kfic2-bodyText'
              id={`timeInDays-${row.applicationID}`}
              data-testid={`timeInDays-${row.applicationID}`}
              sx={{ color: `${colors?.tertiary?.darkGray} !important` }}
            >
              {row.kfnr_sub_status}
            </Typography>
          </Grid>
        );
      },
    },
    {
      field: 'activityCreatedDate',
      sortable: false,
      renderHeader: () => (
        <Typography
          className='kfic2-bodyTextBold'
          id='dateAppliedId'
          data-testid='dateAppliedId'
        >
          {translate('dateApplied')}
        </Typography>
      ),
      flex: 0.8,
      disableColumnMenu: true,
      renderCell: ({ row }: { row: IReqCandidatesListItem }) => {
        return row?.applicationCreatedDate
          ? dateFormat(row.applicationCreatedDate)
          : translate('notAvailable').toString();
      },
    },
    {
      field: 'Take Action',
      renderHeader: () => (
        <Typography
          className='kfic2-bodyTextBold'
          id='action'
          data-testid='action'
        >
          {translate('action')}
        </Typography>
      ),
      sortable: false,
      flex: 1.2,
      disableColumnMenu: true,
      renderCell: ({ row: candidate }: { row: IReqCandidatesListItem }) => {
        if (
          !(
            (candidate?.activityName === HiringActivity.InInterview &&
              candidate?.activityStatus === HMStatusListForInterview.Reject) ||
            (candidate?.activityName === HiringActivity.InScreen &&
              candidate?.activityStatus === HMStatusListForScreen.Reject) ||
            (candidate?.activityName === HiringActivity.Interview &&
              candidate?.activityStatus ===
                HMStatusListForInterview.RejectProcess2) ||
            (candidate?.activityName === HiringActivity.Submit &&
              candidate?.activityStatus === HMStatusListForScreen.Reject)
          )
        ) {
          if (
            candidate?.activityName === HiringActivity.InInterview ||
            candidate?.activityName === HiringActivity.InScreen ||
            candidate?.activityName === HiringActivity.Interview ||
            candidate?.activityName === HiringActivity.Submit
          ) {
            if (!candidate?.currentActivityStatus) {
              return (
                <span className='action-wrap'>
                  <KFButton
                    id='candidate-Decision-makeADecision-btn'
                    data-testid='candidate-Decision-makeADecision-btn'
                    color='primary'
                    aria-label='candidate make a decision button'
                    onClick={() => {
                      navigate(`../${HMRoutingPaths.candidateProfile}`, {
                        state: { candidate },
                      });
                    }}
                  >
                    {candidate?.activityName === HiringActivity.InInterview ||
                    candidate?.activityName === HiringActivity.Interview
                      ? translate('makeADecision')
                      : translate('reviewCandidate')}
                  </KFButton>
                </span>
              );
            }
            return (
              <>
                {getDecisionTag(
                  candidate?.activityName as HiringActivity,
                  candidate?.currentActivityStatus as TMakeDecisionStatus
                )}
              </>
            );
          }
        }
        return <></>;
      },
    },
  ];

  const getRowId = (params: IReqCandidatesListItem) => {
    const uniqueCustomId = `${params.requisitionID}_${params.candidateID}_${
      params.applicationID
    }_${params.activityID}_${params.name?.replaceAll(/\s/g, '')}`;
    return uniqueCustomId;
  };

  const separatorBorder = `1px solid ${colors?.background.darkGray} !important`;

  return (
    <>
      <KFDataTable
        {...props}
        className='dataTable'
        data-testid='detailCandidateList'
        sx={{
          backgroundColor: `${colors?.background.componentBackground}`,
          borderRadius: '1rem !important',
          overflow: 'hidden',
          '& .MuiDataGrid-columnHeaders': {
            borderBottom: separatorBorder,
            boxSizing: 'content-box',
          },
          '& .MuiDataGrid-row': {
            borderTop: separatorBorder,
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: separatorBorder,
          },
        }}
        headerHeight={60}
        rowHeight={72}
        rows={requisitionCandidates ?? []}
        columns={columns}
        getRowId={getRowId}
        rowsPerPageOptions={[25, 50, 100]}
        paginationMode='server'
        disableSelectionOnClick
        loading={showLoader}
        components={{
          NoRowsOverlay: () => (
            <TypographyWithI18N
              i18nKey='noCandidatesFound'
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            />
          ),
        }}
        paginationLabel={{
          'Items per page:': translate('itemsPerPage') + ':',
          of: translate('of'),
          items: translate('items'),
          'more than': translate('moreThan'),
          page: translate('page'),
          pages: translate('pages'),
        }}
      />
      {makingDecisionFor && (
        <MakeDecisionModal
          id='candidate-pipeline-make-decision-modal'
          data={makingDecisionFor}
          open={makeDecisionModalOpen}
          setOpen={setMakeDecisionModalOpen}
        />
      )}
    </>
  );
};

export default withTranslation()(CandidatePipeLineTable);
